/**
 * Helper to transform legacy template send payloads to Phoenix format
 * Phoenix uses /inboxmw/whatsapp/templates/send/list/simplified endpoint
 * for both single and bulk/list sends.
 */


/**
 * Check if a value is a placeholder (attribute reference)
 * Placeholders start with "contact" (e.g., "contact.full_name", "contacts.custom_attributes.field")
 *
 * @param {string} value - The value to check
 * @returns {boolean} True if value is a placeholder
 */
function isPlaceholder(value) {
  if (typeof value !== 'string') return false;
  return value.startsWith('contact.') || value.startsWith('contacts.');
}

/**
 * Normalize placeholder to use "contact." (singular)
 * Backend expects "contact." not "contacts."
 *
 * @param {string} value - The value to normalize
 * @returns {string} Normalized value with "contact." prefix
 */
function normalizePlaceholder(value) {
  if (typeof value !== 'string') return value;
  if (value.startsWith('contacts.')) {
    return value.replace('contacts.', 'contact.');
  }
  return value;
}

/**
 * Wrap placeholder with %% if needed
 * Phoenix expects: %%contact.full_name%% but we store: contact.full_name
 *
 * @param {string} value - The value to process
 * @returns {string} Value with %% wrapper if it's a placeholder
 */
function formatPlaceholder(value) {
  if (!value) return '';

  // If it's already wrapped with %%, return as-is
  if (value.startsWith('%%') && value.endsWith('%%')) {
    return value;
  }

  // If it's a placeholder, normalize and wrap with %%
  if (isPlaceholder(value)) {
    const normalized = normalizePlaceholder(value);
    return `%%${normalized}%%`;
  }

  // Static text - return as-is
  return value;
}

/**
 * Transform legacy links structure to Phoenix translations structure
 *
 * Legacy format:
 * links: [
 *   {
 *     language: "en",
 *     body: [{ number: 1, value: "contact.full_name" }],  // attribute
 *     header: [{ number: 1, value: "https://..." }],      // static
 *     buttons: [{ number: 1, value: "Click" }]            // static
 *   }
 * ]
 *
 * Phoenix format:
 * translations: [
 *   {
 *     "en": {
 *       "variables": {
 *         "body": ["%%contact.full_name%%"],
 *         "header": ["https://..."],
 *         "buttons": ["Click"]
 *       }
 *     }
 *   }
 * ]
 *
 * @param {Array} links - Legacy links array
 * @returns {Array} Phoenix translations array
 */
export function transformLinksToTranslations(links) {
  if (!Array.isArray(links) || links.length === 0) {
    return [];
  }

  return links.map((link) => {
    const language = link.language || 'en';
    const variables = {};

    // Transform body - wrap placeholders with %% and remove format field
    if (Array.isArray(link.body) && link.body.length > 0) {
      const filteredBody = link.body.filter((item) => {
        return (
          item &&
          item.value !== undefined &&
          item.value !== null &&
          item.value !== ''
        );
      });

      if (filteredBody.length) {
        variables.body = filteredBody
          .sort((a, b) => (a.number || 0) - (b.number || 0))
          .map((item) => {
            // Remove format from item (will be in meta.datetime instead)
            const { format, ...cleanItem } = item;
            return formatPlaceholder(cleanItem.value);
          });
      }
    }

    // Transform header - wrap placeholders with %% and remove format field
    if (Array.isArray(link.header) && link.header.length > 0) {
      const filteredHeader = link.header.filter((item) => {
        return (
          item &&
          item.value !== undefined &&
          item.value !== null &&
          item.value !== ''
        );
      });

      if (filteredHeader.length) {
        const headerValues = filteredHeader
          .sort((a, b) => (a.number || 0) - (b.number || 0))
          .map((item) => {
            const { format, ...cleanItem } = item;
            return formatPlaceholder(cleanItem.value);
          });

        if (headerValues.length === 1) {
          [variables.header] = headerValues;
        } else if (headerValues.length > 1) {
          variables.header = headerValues;
        }
      }
    }

    // Transform buttons - wrap placeholders with %% and remove format field
    if (Array.isArray(link.buttons) && link.buttons.length > 0) {
      const filteredButtons = link.buttons.filter((item) => {
        return (
          item &&
          item.value !== undefined &&
          item.value !== null &&
          item.value !== ''
        );
      });

      if (filteredButtons.length) {
        variables.buttons = filteredButtons
          .sort((a, b) => (a.number || 0) - (b.number || 0))
          .map((item) => {
            const { format, ...cleanItem } = item;
            return formatPlaceholder(cleanItem.value);
          });
      }
    }

    return {
      [language]: {
        variables,
      },
    };
  });
}

/**
 * Build contact object for Phoenix API from minimal contact info
 * For single sends, we need to construct a contact object.
 *
 * @param {Object} contactData - Contact information
 * @param {number} contactData.contactId - Contact ID
 * @param {string} contactData.phoneNumber - Contact phone number (with +)
 * @param {number} contactData.accountId - Account ID
 * @param {string} contactData.language - Preferred language (optional)
 * @param {string} contactData.name - Contact name (optional)
 * @param {string} contactData.email - Contact email (optional)
 * @param {Object} contactData.custom_attributes - Contact custom attributes (optional)
 * @param {Object} contactData.additional_attributes - Contact additional attributes (optional)
 * @returns {Object} Phoenix contact object
 */
export function buildPhoenixContactObject(contactData) {
  const {
    contactId,
    phoneNumber,
    accountId,
    language,
    name,
    email,
    custom_attributes,
    additional_attributes,
  } = contactData;

  // Build custom_attributes, ensuring ch_pref_lang is included
  const customAttrs = { ...(custom_attributes || {}) };
  if (language && !customAttrs.ch_pref_lang) {
    customAttrs.ch_pref_lang = language;
  }

  return {
    id: contactId,
    name: name || 'Contact',
    email: email || null,
    phone_number: phoneNumber,
    account_id: accountId,
    additional_attributes: additional_attributes || {},
    identifier: null,
    custom_attributes: customAttrs,
  };
}

/**
 * Build meta.datetime structure from links with format information
 * Extracts datetime fields and their formats to create the meta structure expected by Phoenix
 *
 * @param {Array} links - Array of link objects from different languages
 * @param {string} defaultTimezone - Default timezone (e.g., "Europe/Vienna")
 * @returns {Object|null} Meta datetime object or null if no datetime fields
 */
export function buildDatetimeMeta(links, defaultTimezone = 'UTC') {
  if (!Array.isArray(links) || links.length === 0) {
    return null;
  }

  const datetimeFields = [];
  const seenPaths = new Set();

  // Iterate through all languages and link types to find datetime fields
  links.forEach((languageLinks) => {
    ['header', 'body', 'buttons'].forEach((linkType) => {
      const linksArray = languageLinks[linkType];
      if (Array.isArray(linksArray)) {
        linksArray.forEach((link) => {
          // Check if this link has a format (indicates it's a datetime field)
          if (link.format && link.value) {
            // Normalize path: contacts.* -> contact.*
            const path = normalizePlaceholder(link.value);

            // Only add each path once (avoid duplicates from multiple languages)
            if (!seenPaths.has(path)) {
              seenPaths.add(path);

              datetimeFields.push({
                path,
                input: 'iso', // Default to ISO, could be enhanced to detect unix/unix_ms
                format: link.format,
              });
            }
          }
        });
      }
    });
  });

  // Only return meta if we found datetime fields
  if (datetimeFields.length === 0) {
    return null;
  }

  return {
    datetime: {
      defaultTimezone,
      fields: datetimeFields,
    },
  };
}

/**
 * Transform legacy template send payload to Phoenix format
 *
 * @param {Object} legacyPayload - Legacy payload with data wrapper
 * @param {Object} options - Additional options
 * @param {Object} options.contact - Contact object for single sends (optional)
 * @param {string} options.contact.name - Contact name
 * @param {string} options.contact.email - Contact email
 * @param {string} options.contact.phone_number - Contact phone number
 * @param {string} options.defaultTimezone - Default timezone for datetime formatting (optional, defaults to UTC)
 * @returns {Object} Phoenix-compatible payload
 */
export function transformLegacyToPhoenixPayload(legacyPayload, options = {}) {
  const data = legacyPayload.data || legacyPayload;

  // Automatically bypass spam prevention in dev/staging environments
  const isDevelopment = process.env.FRONTEND_ENV === 'develop';
  const bypassSpamPrevention = data.bypassSpamPrevention ?? isDevelopment;

  const phoenixPayload = {
    accountId: data.clientId,
    templateName: data.templateName,
    from: data.number || data.from,
    bypassSpamPrevention,
  };

  // Transform links to translations
  if (data.links && Array.isArray(data.links)) {
    phoenixPayload.translations = transformLinksToTranslations(data.links);

    // Build meta.datetime from links with format information
    if (!data.meta && !options.meta) {
      const datetimeMeta = buildDatetimeMeta(
        data.links,
        options.defaultTimezone || 'UTC'
      );
      if (datetimeMeta) {
        phoenixPayload.meta = datetimeMeta;
      }
    }
  }

  // Add meta field for datetime formatting if explicitly provided
  if (data.meta) {
    phoenixPayload.meta = data.meta;
  } else if (options.meta) {
    phoenixPayload.meta = options.meta;
  }

  // Determine if this is a single send or list send
  if (data.contactId) {
    // Single send - build contact object
    const language = data.links?.[0]?.language || 'en';

    // Get contact phone number from options
    const phoneNumber = options.contact?.phone_number;
    if (!phoneNumber) {
      throw new Error('Contact phone number is required for single sends');
    }

    // Ensure phone number has + prefix
    const formattedPhone = phoneNumber.startsWith('+')
      ? phoneNumber
      : `+${phoneNumber}`;

    phoenixPayload.contacts = [
      buildPhoenixContactObject({
        contactId: data.contactId,
        phoneNumber: formattedPhone,
        accountId: data.clientId,
        language,
        name: options.contact?.name || 'Contact',
        email: options.contact?.email,
        custom_attributes: options.contact?.custom_attributes,
        additional_attributes: options.contact?.additional_attributes,
      }),
    ];
  } else if (data.listId) {
    // List send - use listId
    phoenixPayload.listId = data.listId;
  } else if (data.contacts) {
    // List send with explicit contacts array (already in Phoenix format)
    phoenixPayload.contacts = data.contacts;
  }

  return phoenixPayload;
}
